import { DownOutlined, SearchOutlined } from '@ant-design/icons'
import { Input, InputRef, Select } from 'antd'
import classNames from 'classnames'
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react'
import { ItemPicker } from '../../components/Pricebook/ItemPicker'
import { Z_INDEX_MOBILE_SELECT } from '../../layout/zIndex'
import { OnsiteModal } from '../../ui/OnsiteModal/OnsiteModal'
import { BzSelectProps } from '../BzSelect/BzSelect'
import { BzSelectMode, BzSelectOption, BzSelectSize, BzSheetSize } from '../BzSelect/BzSelectTypes'
import './BzMobileSelect.less'

type BzMobileSingleSelectProps<T> = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'id' | 'aria-required' | 'aria-invalid' | 'aria-describedby'
> & {
  title: string
  value: T | undefined
  labelOverride?: string
  placeholder?: string
  disabled?: boolean
  options?: BzSelectOption[]
  onChange: (value: T) => void
  size?: BzSelectSize
  sheetSize?: BzSheetSize
  onSearch?: (term: string) => void
  emptyState?: React.ReactNode
  className?: string
  loading?: boolean
  name?: string
  ddActionName?: string
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
  hideInput?: boolean
}

const BzMobileSingleSelectInner = <T extends string>({
  title,
  value,
  labelOverride,
  placeholder,
  options = [],
  onChange,
  disabled,
  size = 'large',
  sheetSize = 'full',
  onSearch,
  emptyState,
  className,
  loading = false,
  name,
  ddActionName,
  open: externalOpen,
  onClose: externalOnClose,
  onOpen: externalOnOpen,
  hideInput,
  id,
  'aria-required': ariaRequired,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: BzMobileSingleSelectProps<T>) => {
  const searchInputRef = React.useRef<InputRef>(null)
  const triggerRef = React.useRef<InputRef>(null)
  const popupId = `mobile-select-${useId().replace(/:/g, '')}-dialog`

  const [internalIsOpen, setInternalIsOpen] = useState(!!externalOpen)
  const isOpen = externalOpen ?? internalIsOpen
  const wasOpen = React.useRef(isOpen)

  useEffect(() => {
    if (isOpen) {
      document
        .getElementById(popupId)
        ?.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled]), [tabindex="0"]')
        ?.focus({ preventScroll: true })
    } else if (wasOpen.current) {
      triggerRef.current?.focus({ preventScroll: true })
    }
    wasOpen.current = isOpen
  }, [isOpen, popupId])

  const openPicker = useCallback(() => {
    if (disabled || isOpen) return
    triggerRef.current?.focus({ preventScroll: true })
    externalOnOpen?.()
    setInternalIsOpen(true)
  }, [disabled, isOpen, externalOnOpen])

  // biome-ignore lint/correctness/useExhaustiveDependencies: searchInputRef is a stable ref object
  useLayoutEffect(() => {
    if (onSearch && isOpen && searchInputRef.current) {
      // This setTimeout is a hack to flush the focus at the end of the event loop.
      // For whatever reason without it, calling focus on the ref doesn't do the trick.
      setTimeout(() => {
        // Additional safety check for mobile compatibility
        if (searchInputRef.current && typeof searchInputRef.current.focus === 'function') {
          try {
            searchInputRef.current.focus()
          } catch (error) {
            // Silently handle focus errors on mobile devices
            console.warn('Mobile focus error (non-critical):', error)
          }
        }
      }, 1)
    }
  }, [onSearch, isOpen, searchInputRef])

  const label = useMemo(() => options.find(option => option.value === value)?.label, [options, value])

  const onClose = useCallback(() => {
    setInternalIsOpen(false)
    externalOnClose?.()
  }, [externalOnClose])
  return (
    <>
      <div
        onClickCapture={e => {
          e.preventDefault()
          e.stopPropagation()
          openPicker()
        }}
      >
        <Input
          ref={triggerRef}
          id={id}
          aria-required={ariaRequired}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={classNames(
            {
              hidden: hideInput,
            },
            className,
          )}
          value={labelOverride ?? label ?? value}
          aria-label={title}
          role="combobox"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? popupId : undefined}
          onKeyDown={event => {
            if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
              event.preventDefault()
              openPicker()
            }
          }}
          readOnly
          data-testid={name}
          data-dd-action-name={ddActionName}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          prefix={onSearch ? <SearchOutlined className="text-bz-gray-700" /> : undefined}
          suffix={!onSearch && <DownOutlined className="text-[rgba(0,0,0,0.25)]" />}
        />
      </div>
      <OnsiteModal
        dialogId={popupId}
        ariaLabel={isOpen ? title : undefined}
        drawer={sheetSize === 'half'}
        onClose={onClose}
        modalZIndexClassName={`z-[${Z_INDEX_MOBILE_SELECT}]`}
        open={isOpen}
        size={sheetSize === 'half' ? 'small' : 'large'}
      >
        <ItemPicker
          canSelectMultiple={false}
          title={title}
          items={options.map(option => ({
            id: option.value,
            name: option.label,
            customNode: option.customNode,
            disabled: option.disabled,
            value: 0,
          }))}
          hideSearch={sheetSize === 'half'}
          onCancel={onClose}
          onSearch={onSearch}
          searchInputRef={searchInputRef}
          selectedItemCountMap={
            value
              ? {
                  [value]: 1,
                }
              : {}
          }
          onItemSelect={id => {
            onChange(id as T)
            onClose()
          }}
          hideFooter
          showItemValue={false}
          emptyState={emptyState}
          loading={loading}
        />
      </OnsiteModal>
    </>
  )
}

BzMobileSingleSelectInner.displayName = 'BzMobileSingleSelectInner'

export const BzMobileSingleSelect = React.memo(BzMobileSingleSelectInner) as typeof BzMobileSingleSelectInner

type BzMobileMultiSelectProps<T> = {
  title: string
  values: T[] | []
  placeholder?: string
  disabled?: boolean
  options?: BzSelectOption[]
  onChange: (values: T[]) => void
  tagRender?: (props: {
    value: T
    label: React.ReactNode
    onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void
  }) => React.ReactElement
  size?: BzSelectSize
  sheetSize?: BzSheetSize
  className?: string
  name?: string
  ddActionName?: string
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
  hideInput?: boolean
}

const BzMobileMultiSelectInner = <T extends string>({
  title,
  values = [],
  placeholder,
  options = [],
  onChange,
  disabled,
  tagRender,
  size = 'large',
  sheetSize = 'full',
  className,
  name,
  ddActionName,
  open: externalOpen,
  onClose: externalOnClose,
  onOpen: externalOnOpen,
  hideInput,
}: BzMobileMultiSelectProps<T>) => {
  const [internalIsOpen, setInternalIsOpen] = useState(!!externalOpen)
  const isOpen = externalOpen ?? internalIsOpen

  const [selectedItemCountMap, setSelectedItemCountMap] = useState(
    values.reduce(
      (acc, value) => {
        acc[value as string] = 1
        return acc
      },
      {} as Record<string, number>,
    ),
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedItemCountMap(
        values.reduce(
          (acc, value) => {
            acc[value as string] = 1
            return acc
          },
          {} as Record<string, number>,
        ),
      )
    }
  }, [isOpen, values])

  const onClose = useCallback(() => {
    setInternalIsOpen(false)
    externalOnClose?.()
  }, [externalOnClose])
  return (
    <>
      <Select
        className={classNames(
          'bz-mobile-multi-select',
          {
            hidden: hideInput,
          },
          className,
        )}
        popupClassName="bz-mobile-multi-select-popup"
        size={size}
        mode="multiple"
        placeholder={placeholder}
        onDeselect={value => {
          onChange(values.filter(v => v !== value))
          setSelectedItemCountMap(prev => {
            const { [value]: _, ...rest } = prev
            return rest
          })
        }}
        title={title}
        data-testid={name}
        data-dd-action-name={ddActionName}
        aria-label={title}
        aria-roledescription="Select Button"
        tagRender={tagRender}
        disabled={disabled}
        value={values}
        options={options.map(option => ({
          label: option.label,
          value: option.value,
        }))}
        open={false}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          if (disabled) {
            return
          } else {
            externalOnOpen?.()
            setInternalIsOpen(true)
          }
        }}
      />
      <OnsiteModal
        drawer={sheetSize === 'half'}
        onClose={onClose}
        modalZIndexClassName={`z-[${Z_INDEX_MOBILE_SELECT}]`}
        open={isOpen}
        size={sheetSize === 'half' ? 'small' : 'default'}
      >
        <ItemPicker
          canSelectMultiple
          submitText="Save"
          title={title}
          items={options.map(option => ({
            id: option.value?.toString() ?? '',
            name: option.label?.toString() ?? '',
            customNode: option.customNode,
            disabled: option.disabled,
            value: 0,
          }))}
          hideSearch={sheetSize === 'half'}
          onCancel={() => {
            onClose()
            setSelectedItemCountMap({})
          }}
          selectedItemCountMap={selectedItemCountMap}
          onItemSelect={(id, count) => {
            setSelectedItemCountMap(prev => ({
              ...prev,
              [id as string]: count,
            }))
          }}
          onSave={() => {
            onChange(Object.keys(selectedItemCountMap).filter(key => selectedItemCountMap[key] > 0) as T[])
            onClose()
          }}
          showItemValue={false}
          hideMultipleQuantityPicker
        />
      </OnsiteModal>
    </>
  )
}

export const BzMobileMultiSelect = React.memo(BzMobileMultiSelectInner) as typeof BzMobileMultiSelectInner

const BzMobileSelect = <SelectMode extends BzSelectMode = 'default', T extends string = ''>(
  props: BzSelectProps<SelectMode, T>,
) => {
  const { mode = 'default' } = props
  if (mode === 'default') {
    return <BzMobileSingleSelect {...(props as BzSelectProps<'default', T>)} />
  }
  return <BzMobileMultiSelect {...(props as BzSelectProps<'multiple', T>)} />
}

const MemoizedBzMobileSelect = React.memo(BzMobileSelect) as typeof BzMobileSelect

export default MemoizedBzMobileSelect
